import { useRef } from "react";
import { useLocation } from "wouter";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards, Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import type { Project } from "@shared/types";
import "swiper/css";
import "swiper/css/effect-cards";
import "swiper/css/pagination";

interface BrowserStackCarouselProps {
  projects: Project[];
  intervalMs?: number;
}

function hostnameOf(url: string, fallback: string): string {
  if (!url) return fallback;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return fallback;
  }
}

function BrowserCard({ project }: { project: Project }) {
  const host = hostnameOf(project.url, project.slug + ".co.il");

  return (
    <div className="rounded-2xl overflow-hidden bg-[#0F172A] border border-white/10 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] h-full">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-b from-[#1a2236] to-[#131b2c] border-b border-white/[0.06]">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
          <div className="w-3 h-3 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 mx-3 px-3 py-1 bg-black/30 rounded-md text-xs text-white/50 text-center font-mono truncate">
          {host}
        </div>
      </div>
      <div className="aspect-[16/10] bg-[#0B1120] overflow-hidden">
        {project.image ? (
          <img
            src={project.image}
            alt={project.name}
            loading="lazy"
            draggable={false}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 text-sm">
            {project.name}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrowserStackCarousel({
  projects,
  intervalMs = 3500,
}: BrowserStackCarouselProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [, setLocation] = useLocation();

  if (!projects.length) return null;

  const handleSlideClick = (swiper: SwiperType) => {
    if (!(swiper as unknown as { allowClick: boolean }).allowClick) return;
    const project = projects[swiper.realIndex];
    if (project) setLocation(`/projects/${project.slug}`);
  };

  return (
    <div className="relative w-full max-w-[520px] mx-auto" dir="ltr">
      <div className="absolute inset-0 -z-10 bg-[#66CDAA]/10 rounded-full blur-[80px]" />

      <Swiper
        modules={[EffectCards, Autoplay, Pagination]}
        effect="cards"
        grabCursor
        loop={projects.length > 2}
        autoplay={{ delay: intervalMs, disableOnInteraction: false, pauseOnMouseEnter: true }}
        pagination={{ clickable: true, el: ".browser-stack-pagination" }}
        cardsEffect={{ slideShadows: false, perSlideOffset: 10, perSlideRotate: 3 }}
        onSwiper={(s) => (swiperRef.current = s)}
        onClick={handleSlideClick}
        className="!overflow-visible"
      >
        {projects.map((project) => (
          <SwiperSlide key={project.id ?? project.slug} className="!rounded-2xl cursor-pointer">
            <BrowserCard project={project} />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="browser-stack-pagination flex justify-center gap-2 mt-6 [&_.swiper-pagination-bullet]:w-1.5 [&_.swiper-pagination-bullet]:h-1.5 [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:bg-white/20 [&_.swiper-pagination-bullet]:transition-all [&_.swiper-pagination-bullet]:cursor-pointer [&_.swiper-pagination-bullet-active]:!bg-[#66CDAA] [&_.swiper-pagination-bullet-active]:!w-6" />
    </div>
  );
}
